import Popover from "components/Popover/Popover";
import ToolboxButton, {
  ToolboxButtonIcon,
} from "Dashboard/components/Buttons/ToolboxButton";
import { SearchType } from "Dashboard/domain/SearchType";
import { IOutputSearch } from "Dashboard/scenes/ConcreteBase/domain/IOutputSearch";
import classesToolbox from "Dashboard/scenes/ConcreteBase/styles/toolbox.module.scss";
import { useAppDispatch } from "hooks/hooks";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { Control, useForm, UseFormGetValues, UseFormHandleSubmit, UseFormRegister, UseFormReset, UseFormSetFocus, UseFormSetValue } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  getOutputs,
  selectState,
  setEditableItemInventoryPopover,
  setSearchPopover
} from "../outputSlice";
import EditableItemInventoryPopover from "./EditableItemInventorytPopover";
import PrintStateOfOutput from "./PrintStateOfOutput";
import SearchPopover from "./SearchPopover";
import { usePagePrint } from "hooks/usePagePrint";
import { areQueryParamsEmpty } from "utils/queryParamsUtil";
import { useLocalStorage } from "hooks/LocalStorage";

interface IProps {
  defaultGetOutputListItemsSearchParams: IOutputSearch;
  setGetOutputListItemsSearchParams: Dispatch<SetStateAction<IOutputSearch>>;
  registerSearch: UseFormRegister<IOutputSearch>;
  handleSubmitSearch: UseFormHandleSubmit<IOutputSearch>;
  resetSearch: UseFormReset<IOutputSearch>;
  controlSearch: Control<IOutputSearch, any>;
  setFocusSearch: UseFormSetFocus<IOutputSearch>;
  getValuesSearch: UseFormGetValues<IOutputSearch>;
  setValueSearch: UseFormSetValue<IOutputSearch>;
};

const Toolbox = ({
  defaultGetOutputListItemsSearchParams,
  setGetOutputListItemsSearchParams,
  registerSearch,
  handleSubmitSearch,
  resetSearch,
  controlSearch,
  setFocusSearch,
  getValuesSearch,
  setValueSearch,
}: IProps) => {
  const componentRef = useRef<HTMLDivElement>(null);
  const {
    searchPopover, editableItemInventoryPopover
  } = useSelector(selectState);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const handlePrint = usePagePrint(componentRef, { margins: "0.5cm" });

  const [searchTypeValue, setSearchTypeValue] = useState(SearchType.BASIC);
  const [storedSearchTerm, setStoredSearchTerm]
    = useLocalStorage("cbOutputSearchQuery", "");
  const [storedSearchType, setStoredSearchType]
    = useLocalStorage("cbOutputSearchType", SearchType.BASIC);
  const [storedSearchInputs, setStoredSearchInputs]
    = useLocalStorage("cbOutputSearchInputs", null);
  const isSearchFormEmpty = areQueryParamsEmpty(getValuesSearch());

  useEffect(() => {
    if (storedSearchType === "BASIC") {
      setGetOutputListItemsSearchParams({ searchQuery: storedSearchTerm });
    }
    if (storedSearchType === "ADVANCED") {
      storedSearchInputs.searchQuery = undefined;
      setGetOutputListItemsSearchParams(storedSearchInputs);
    }
  }, [
    setValueSearch,
    storedSearchTerm,
    storedSearchType,
    storedSearchInputs
  ]);

  useEffect(() => {
    if (storedSearchTerm) {
      setValueSearch("searchQuery", storedSearchTerm);
    }
    if (storedSearchType) {
      setSearchTypeValue(storedSearchType);
    }
    if (storedSearchInputs) {
      Object.keys(storedSearchInputs).forEach((key: any) => {
        if (key !== "searchQuery") {
          setValueSearch(key, storedSearchInputs[key]);
        }
      });
    }
  }, [
    setValueSearch,
    storedSearchTerm,
    storedSearchType,
    storedSearchInputs
  ]);

  const handleNavigation = (path: string) => {
    const currentSearchTerm = getValuesSearch("searchQuery");
    const currentSearchInputs = getValuesSearch();
    setStoredSearchTerm(currentSearchTerm);
    setStoredSearchType(searchTypeValue);
    setStoredSearchInputs(currentSearchInputs);
    navigate(path);
  };

  const handleReset = () => {
    setGetOutputListItemsSearchParams(defaultGetOutputListItemsSearchParams);
    resetSearch();
  };

  return (
    <>
      <nav className="container nav-arrows">
        <div>
          <ToolboxButton
            icon={ToolboxButtonIcon.Back}
            tooltip="Betonska Baza - Ulaz"
            toolbox={true}
            onClick={() => handleNavigation('/betonska-baza')}
          />
        </div>
        <div>
          <ToolboxButton
            icon={ToolboxButtonIcon.Search}
            tooltip="Traži"
            toolbox={true}
            onClick={() => dispatch(setSearchPopover({ isOpen: true }))}
          />
          {!isSearchFormEmpty
            && (<ToolboxButton
              icon={ToolboxButtonIcon.CancelSearch}
              tiny={true}
              tooltip="Pokaži sve"
              onClick={() => handleReset()}
            />)}
          <ToolboxButton
            icon={ToolboxButtonIcon.Print}
            tooltip="Štampa"
            toolbox={true}
            onClick={handlePrint}
          />
        </div>

        {/* <!-- Nova verzija pretrage --> */}
        <div ref={componentRef} className="print-area">
          <PrintStateOfOutput />
        </div>
        <div className={classesToolbox.right}>
          <ToolboxButton
            icon={ToolboxButtonIcon.Hamburger}
            tooltip="Šifarnik"
            toolbox={true}
            onClick={(event) => dispatch(
              setEditableItemInventoryPopover({
                isOpen: true,
                top: event.clientY,
                left: event.clientX
              }))}
          />
        </div>
      </nav>
      <Popover
        isOpen={searchPopover.isOpen}
        onClose={() => dispatch(setSearchPopover({ isOpen: false }))}
        title="Pretraga"
        direction="right"
        arrowStyle={{ top: "42px" }}
        position={{ top: 10, left: 190 }}
      >
        <SearchPopover
          register={registerSearch}
          control={controlSearch}
          handleSubmit={handleSubmitSearch}
          setFocus={setFocusSearch}
          searchType={searchTypeValue}
          setSearchType={setSearchTypeValue}
          getValues={getValuesSearch}
          setValue={setValueSearch}
          reset={resetSearch}
          setGetOutputListItemsSearchParams={setGetOutputListItemsSearchParams}
        />
      </Popover>
      <Popover
        isOpen={editableItemInventoryPopover.isOpen}
        onClose={() => dispatch(setEditableItemInventoryPopover({
          isOpen: false
        }))}
        title="Šifarnik"
        direction="left"
        arrowStyle={{ top: "42px" }}
        position={{
          top: editableItemInventoryPopover.top,
          left: editableItemInventoryPopover.left,
          offsetLeft: -540,
          offsetTop: -55,
        }}
      >
        <EditableItemInventoryPopover />
      </Popover>
    </>
  );
};

export default Toolbox;
